import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  Button,
  styled,
} from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isBefore, startOfDay, startOfMonth, endOfMonth, addDays } from 'date-fns';
import { ro } from 'date-fns/locale';
import { getAvailableSlots } from '../../services/api';
import type { Service } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';

const GlassContainer = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.15)',  // Frosted glass effect
  backdropFilter: 'blur(20px)',
  borderRadius: 16,
  border: '1px solid rgba(255, 255, 255, 0.18)',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
  padding: theme.spacing(3),
  position: 'relative',
  overflow: 'hidden',
  height: '100%',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
    zIndex: 0,
  }
}));

const TimeButton = styled(Button)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.18)',
  borderRadius: 8,
  color: '#2A2A2A',  // Dark charcoal
  transition: 'all 0.3s ease',
  '&:hover': {
    background: '#B83232',  // Rich burgundy
    color: '#FFFFFF',
    borderColor: '#B83232',
    boxShadow: '0 4px 15px rgba(184, 50, 50, 0.2)',
  },
  '&.Mui-selected': {
    background: '#B83232',  // Rich burgundy
    color: '#FFFFFF',
    borderColor: '#B83232',
  },
  '&:disabled': {
    background: 'rgba(75, 75, 75, 0.1)',
    color: 'rgba(42, 42, 42, 0.4)',
    border: '1px solid rgba(75, 75, 75, 0.2)',
  }
}));

interface AppointmentCalendarProps {
  barberId: number;
  service: Service;
  onTimeSelect: (date: string, time: string) => void;
}

const AppointmentCalendar: React.FC<AppointmentCalendarProps> = ({
  barberId,
  service,
  onTimeSelect,
}) => {
  const { t, language } = useLanguage();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [disabledDates, setDisabledDates] = useState<Record<string, boolean>>({});

  // Add this useEffect to fetch slots for the initial date
  useEffect(() => {
    if (selectedDate) {
      fetchDaySlots(selectedDate);
    }
  }, []); // Run only once on mount

  // Fetch available slots for the entire month
  const fetchMonthSlots = async (date: Date) => {
    setLoading(true);
    try {
      const start = startOfMonth(date);
      const end = endOfMonth(date);

      const monthSlots = await getAvailableSlots(
        barberId,
        service.id,
        format(start, 'yyyy-MM-dd'),
        format(end, 'yyyy-MM-dd')
      );

      // Create a map of dates that have no available slots
      const newDisabledDates: Record<string, boolean> = {};
      const datesWithSlots = new Set(monthSlots.map(slot => slot.date));

      let currentDate = start;
      while (currentDate <= end) {
        const dateStr = format(currentDate, 'yyyy-MM-dd');
        newDisabledDates[dateStr] = !datesWithSlots.has(dateStr);
        currentDate = addDays(currentDate, 1);
      }

      setDisabledDates(newDisabledDates);
    } catch (error) {
      console.error('Error fetching month slots:', error);
      setError('Failed to fetch available slots');
    } finally {
      setLoading(false);
    }
  };

  // Fetch detailed slots for a specific date
  const fetchDaySlots = async (date: Date) => {
    try {
      setLoading(true);
      const formattedDate = format(date, 'yyyy-MM-dd');

      const response = await getAvailableSlots(
        barberId,
        service.id,
        formattedDate,
        formattedDate
      );

      // Extract just the time strings from the response
      const times = response.filter(slot => slot.date === formattedDate)
        .map(slot => slot.time);
      setAvailableSlots(times);
    } catch (error) {
      console.error('Error fetching day slots:', error);
      setError('Failed to fetch available slots');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch for the current month
  useEffect(() => {
    fetchMonthSlots(selectedDate);
  }, [barberId, service.id]);

  // Handle month change
  const handleMonthChange = (date: Date) => {
    fetchMonthSlots(date);
  };

  // Handle date selection
  const handleDateChange = (date: Date | null) => {
    if (date) {
      setSelectedDate(date);
      fetchDaySlots(date);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <GlassContainer>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={language === 'ro' ? ro : undefined}>
              <DateCalendar
                value={selectedDate}
                onChange={handleDateChange}
                onMonthChange={handleMonthChange}
                loading={loading}
                shouldDisableDate={(date) => {
                  const formattedDate = format(date, 'yyyy-MM-dd');
                  return isBefore(date, startOfDay(new Date())) || disabledDates[formattedDate];
                }}
                sx={{
                  width: '100%',  // Make sure it takes full width
                  maxWidth: '400px',  // Limit the width for larger screens
                  margin: '0 auto',
                  '& .MuiPickersDay-root': {
                    color: '#2A2A2A',
                    '&:hover': {
                      background: 'rgba(184, 50, 50, 0.1)',
                    },
                    '&.Mui-selected': {
                      background: '#B83232',
                      color: '#FFFFFF',
                      '&:hover': {
                        background: '#932828',
                      },
                    },
                    '&.Mui-disabled': {
                      color: 'rgba(42, 42, 42, 0.4)',
                    },
                  },
                  '& .MuiPickersDay-today': {
                    border: '1px solid #B83232',
                  },
                  '@media (max-width: 600px)': {
                    // Responsive adjustments for xs screens
                    maxWidth: '100%',  // Full width on small screens
                    fontSize: '0.875rem',  // Smaller font size
                    '& .MuiPickersDay-root': {
                      padding: '4px',  // Reduce padding for better fit
                    },
                    '& .MuiPickersDay-today': {
                      borderWidth: '1px',  // Ensure border is visible on small screens
                    },
                  }
                }}
              />
            </LocalizationProvider>
          </GlassContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <GlassContainer>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: '#2C3E50',  // Dark navy blue
                fontWeight: 500,
                mb: 2
              }}
            >
              {t('datetime.availableTimes')} {format(selectedDate, 'MMMM d, yyyy')}
            </Typography>

            {loading ? (
              <Typography sx={{ color: '#4B4B4B' }}>{t('app.loading')}</Typography>
            ) : availableSlots.length > 0 ? (
              <Grid container spacing={2}>
                {availableSlots.map((timeSlot) => (
                  <Grid item xs={4} sm={4} md={6} lg={4} key={timeSlot}>
                    <TimeButton
                      variant="outlined"
                      onClick={() => onTimeSelect(format(selectedDate, 'yyyy-MM-dd'), timeSlot)}
                      fullWidth
                    >
                      {timeSlot}
                    </TimeButton>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography sx={{ color: '#4B4B4B' }}>
                {t('datetime.noSlots')} {t('datetime.selectAnother')}
              </Typography>
            )}
          </GlassContainer>
        </Grid>
      </Grid>
      
      {error && (
        <Box sx={{ mt: 3 }}>
          <Typography
            color="error"
            sx={{
              color: '#B83232',  // Burgundy for errors
              background: 'rgba(184, 50, 50, 0.1)',
              p: 2,
              borderRadius: 1,
              textAlign: 'center'
            }}
          >
            {error}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AppointmentCalendar; 